/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1679875200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Mar 27 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "chameleon",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Chameleon",
          "depth": 0,
          "id": "connect-to-chameleon"
        }, {
          "value": "Authenticate with credentials",
          "depth": 1,
          "id": "authenticate-with-credentials"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Users",
          "depth": 1,
          "id": "users"
        }, {
          "value": "Companies",
          "depth": 1,
          "id": "companies"
        }, {
          "value": "Events",
          "depth": 1,
          "id": "events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger-1"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      h3: "h3",
      ul: "ul",
      li: "li",
      h4: "h4"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Power your Chameleon workspace with up-to-date user data straight from your data warehouse. You can sync enriched user data and events from your warehouse, specifically to augment product experiences. Moreover, you can ensure that the data in Chameleon is always up-to-date and in the expected format for your Product and Growth teams."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Users"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to any user object"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Companies"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to any company object"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to any event"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-chameleon",
        children: "Connect to Chameleon"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), "\nbutton. Select ", _jsx(_components.strong, {
          children: "Chameleon"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Chameleon"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-credentials",
        children: "Authenticate with credentials"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Chameleon API Key"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Chameleon destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull\ndata from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview\npage"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and\nthe Chameleon destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "users",
        children: "Users"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source into your Chameleon users."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows in your model with users in Chameleon on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "User uid"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Chameleon custom fields."
      }), "\n", _jsx(_components.h3, {
        id: "companies",
        children: "Companies"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source into your Chameleon companies."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows in your model with users in Chameleon on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Company Uid"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Chameleon custom fields."
      }), "\n", _jsx(_components.h3, {
        id: "events",
        children: "Events"
      }), "\n", _jsx(_components.p, {
        children: "Insert events into Chameleon by user id."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "When sending events to Chameleon you can only specify the event name and user id."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch provides complete visibility into the API calls made during each of your sync runs. We recommend reading our\n", _jsx(_components.a, {
          href: "/syncs/debugger",
          children: "article on debugging tips and tricks"
        }), " to learn more."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger-1",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
